import { formatMoney, useGraphQLQuery } from '@betterment/js-runtime';
import { Banner, Box, Card, Heading, Separator, Text, VStack } from '@betterment/samba-react';
import { CardLoader, ReturnsText } from '@betterment/shared-components';

import {
  DirectTradingAccountSecurityPositionsDocument,
  DirectTradingAccountSecurityPositionsQuery,
} from './graphql/directTradingAccountSecurityPositions.generated';

import { Props } from '../../AccountSecurityPage';

type SecurityPosition = DirectTradingAccountSecurityPositionsQuery['account']['securityPosition'];

const EmptyValueText = () => (
  <Text variant="text50" color="text.weak">
    —
  </Text>
);

const SecurityPositionDetails: React.FC<{ securityPosition: NonNullable<SecurityPosition> }> = props => {
  const { securityPosition } = props;
  const { shares, amount, totalCost, averageCost, totalReturn, todaysReturn } = securityPosition;

  return (
    <>
      <VStack css={{ gap: 'space.300' }}>
        <VStack>
          <Text role="cell" variant="text50" color="text.weak">
            Market value
          </Text>
          <Text variant="text300">{formatMoney(amount)}</Text>
          <Text variant="text50" color="text.weak">
            {shares} shares
          </Text>
        </VStack>
        <Box
          css={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 'space.200', alignItems: 'start' }}
          role="table"
          aria-label="fundamentals"
        >
          <VStack>
            <Text variant="text50" color="text.weak">
              Avg share cost
            </Text>
            {averageCost ? (
              <Text variant="text50" color="text">
                {formatMoney(averageCost)}
              </Text>
            ) : (
              <EmptyValueText />
            )}
          </VStack>
          <VStack>
            <Text variant="text50" color="text.weak">
              Total cost
            </Text>
            {totalCost ? (
              <Text variant="text50" color="text">
                {formatMoney(totalCost)}
              </Text>
            ) : (
              <EmptyValueText />
            )}
          </VStack>
          <VStack>
            <Text variant="text50" color="text.weak">
              Total gain/loss
            </Text>
            {totalReturn ? (
              <ReturnsText amount={totalReturn.amount} percent={totalReturn.percent} />
            ) : (
              <EmptyValueText />
            )}
          </VStack>
          <VStack>
            <Text variant="text50" color="text.weak">
              Today's gain/loss
            </Text>
            {todaysReturn ? (
              <ReturnsText amount={todaysReturn.amount} percent={todaysReturn.percent} />
            ) : (
              <EmptyValueText />
            )}
          </VStack>
        </Box>
      </VStack>
    </>
  );
};

const PositionInfoBanner = ({ securityPosition }: { securityPosition?: SecurityPosition }) => {
  if (!securityPosition) return null;

  const { hasPendingOrders, hasPendingCostBasis } = securityPosition;

  const text = (() => {
    if (hasPendingOrders) return 'Order pending';
    if (hasPendingCostBasis) {
      return 'Cost basis from a recent transfer is pending. This missing data will impact cost and gain/loss data.';
    }
    return null;
  })();

  if (!text) return null;

  return (
    <Banner variant="info" css={{ marginBottom: 'space.200' }}>
      <Banner.Content>
        <Text as="p">{text}</Text>
      </Banner.Content>
    </Banner>
  );
};

export const SecurityPositionDetailsCard: React.FC<Props> = ({ accountId, securityId }) => {
  const { data } = useGraphQLQuery(DirectTradingAccountSecurityPositionsDocument, {
    variables: { accountId, securityId },
    throwOnError: true,
  });

  if (!data) return <CardLoader />;

  const { securityPosition } = data.account;

  return (
    <Card css={{ display: 'grid', gap: 'space.300', padding: 'space.300' }}>
      <Card.Header>
        <Heading as="h2" variant="title200">
          Your holdings
        </Heading>
      </Card.Header>
      <Separator />
      <Card.Content css={{ gap: 'space.50', display: 'grid' }}>
        <PositionInfoBanner securityPosition={securityPosition} />
        {securityPosition && securityPosition.shares > 0 ? (
          <SecurityPositionDetails securityPosition={securityPosition} />
        ) : (
          <Text>Purchased securities will show here.</Text>
        )}
      </Card.Content>
    </Card>
  );
};
